import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';

import appConfig from '../../../app.config';

import { setVolume, mute, unmute, rateItem } from '../../slices/local/orchestrator';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { useRateSongMutation } from '../../slices/api/auth';
import { selectChannelState, selectConnection, selectPlayers, selectRatedSongs } from '../../state/selectors';
import { debounce } from '../../helpers/debounce';
import { getPlayer } from '../../middlewares/orchestrator';
import { useModal } from '../../contexts/modal';
import { VolumeRequestModal } from '../../views/dialogs/VolumeRequestModal';
import { RateSongModal } from '../../views/dialogs/RateSongModal';
import { Cube } from '../../players';

import volumeMute from '../../styles/assets/images/VolumeMute.svg';
import volumeLow from '../../styles/assets/images/VolumeLow.svg';
import volumeMedium from '../../styles/assets/images/VolumeMedium.svg';
import volumeHigh from '../../styles/assets/images/VolumeHigh.svg';

const setVolumeDebounced = debounce((({ dispatch, playerId, channel, volume }: { dispatch: any; playerId: number; channel: number; volume: number }) => {
  dispatch(setVolume({
    playerId,
    channel,
    volume
  }));
}) as unknown as () => void, 20);

const getVolumeIcon = (volume: number) => {
  if (volume === 0) {
    return { volumeIconSrc: volumeMute, volumeTitle: 'Volume Mute' };
  }
  if (volume > 0 && volume <= 30) {
    return { volumeIconSrc: volumeLow, volumeTitle: 'Volume Low' };
  }
  if (volume > 30 && volume <= 60) {
    return { volumeIconSrc: volumeMedium, volumeTitle: 'Volume Medium' };
  }
  if (volume > 60 && volume <= 100) {
    return { volumeIconSrc: volumeHigh, volumeTitle: 'Volume High' };
  }
  return { volumeIconSrc: volumeMute, volumeTitle: 'Volume Mute' };
};

export const useVolumeFooterViewModel = (playerId: number, channel: number) => {
  const dispatch = useAppDispatch();
  const players = useAppSelector(selectPlayers);
  const connection = useAppSelector(selectConnection);
  const channelState = useAppSelector(state => selectChannelState(state, { playerId, channel })) || {};
  const [rateSong] = useRateSongMutation();
  const { muted, volume: channelVolume, queue } = channelState;
  const [volume, setTempVolume] = useState(channelVolume);
  const { showModal } = useModal();
  const [volumeRequest, setVolumeRequest] = useState<any>(null);
  const [isVolumeTooltipOpen, setIsVolumeTooltipOpen] = useState(false)
  const [rateSongRequest, setRateSongRequest] = useState<any>(null);
  const ratedSongs = useAppSelector(selectRatedSongs);
  const playlist = queue?.entries || [];
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressSeconds, setProgressSeconds] = useState(0);
  const channelStateRef = useRef(channelState);
  const progressRef = useRef({ percent: 0, seconds: 0 });
  const player = players[playerId];

  let currentSongEntry: any = playlist.find(e => e.playing);
  if (player !== undefined && player.isOnline && channelState.cubeEvents !== undefined && channelState.cubeEvents.length > 0 && player.type == Cube) {
    currentSongEntry = { ...(channelState.cubeEvents[0].song as any), playing: true };
  }

  useEffect(() => {
    channelStateRef.current = channelState;
  }, [channelState]);

  useEffect(() => {
    const setProgress = ({ percent, seconds }: { percent: number; seconds: number }) => {
      if (progressRef.current.percent === percent && progressRef.current.seconds === seconds) {
        return;
      }

      progressRef.current = { percent, seconds };
      setProgressPercent(percent);
      setProgressSeconds(seconds);
    };

    const interval = setInterval(() => {
      const currentChannelState = channelStateRef.current;
      const currentQueue = currentChannelState.queue;
      const currentPlaylist = currentQueue?.entries || [];
      const currentEntry = currentPlaylist.find(entry => entry.playing);

      if (currentEntry) {
        const playerProgress = getPlayer(currentChannelState, playerId, channel);
        if (playerProgress) {
          const progress = playerProgress.progress(currentQueue.playIndex);
          setProgress({
            percent: progress.percent,
            seconds: Math.round(progress.seconds)
          });
        }
      } else {
        setProgress({ percent: 0, seconds: 0 });
      }
    }, 500);

    return () => clearInterval(interval);
  }, [playerId, channel]);

  useEffect(() => {
    setTempVolume(channelState.volume);
  }, [channelState]);

  useEffect(() => {
    if (volumeRequest !== null) {
      showModal(VolumeRequestModal, {
        volume: volumeRequest.volume,
        onConfirm: () => {
          dispatch(setVolume({
            ...volumeRequest,
            defer: true
          }));

          setTempVolume(volumeRequest.volume);
        },
        onCancel: () => {
          setTempVolume(channelState.volume);
        }
      });
    }
  }, [volumeRequest]);

  useEffect(() => {
    if (rateSongRequest !== null && connection.available && player.isOnline && !ratedSongs.includes(rateSongRequest.songId)) {
      showModal(RateSongModal, {
        ...rateSongRequest,
        onConfirm: ({ comment }: { comment: string }) => {
          rateSong({
            ...rateSongRequest,
            comment
          });

          dispatch(rateItem({
            playerId,
            channel,
            index: rateSongRequest.playIndex,
            songId: rateSongRequest.songId
          }));
        }
      })
    }
  }, [rateSongRequest])

  const triggerMuted = () => {
    if (muted) {
      dispatch(unmute({
        playerId,
        channel
      }));
    } else {
      dispatch(mute({
        playerId,
        channel
      }));
    }
  };

  const rate = (like: boolean) => {
    if (appConfig.features.rating && connection.available && player.isOnline) {
      let currentSong: any = playlist.find(e => e.playing);
      if (player.type == Cube) {
        currentSong = channelState.cubeEvents![0].song;
      }
      if (currentSong) {
        setRateSongRequest({
          songId: currentSong.persistentId,
          stationId: channelState.stationId,
          like,
          songName: `${currentSongEntry.artist} - ${currentSongEntry.title}`,
          playIndex: playlist.findIndex(e => e.playing)
        });
      }
    }
  };

  const changeVolume = (e: any) => {
    const playerInstance = getPlayer(channelState, playerId, channel);
    let newVolume = (parseInt(e.target.value) - 1) || 0;
    newVolume = Math.min(Math.max(newVolume, 0), 100);

    setTempVolume(newVolume);

    if (!playerInstance.requiresDeferredVolumeChange()) {
      // We decouple setting volumes to avoid a stuttering UI
      setVolumeDebounced({ dispatch, playerId, channel, volume: newVolume });
    }
  };

  const postChangeVolume = (e: any) => {
    const playerInstance = getPlayer(channelState, playerId, channel);
    let newVolume = (parseInt(e.target.value) - 1) || 0;
    newVolume = Math.min(Math.max(newVolume, 0), 100);

    if (playerInstance.requiresDeferredVolumeChange()) {
      setVolumeRequest({
        playerId,
        channel,
        volume: newVolume
      });
    }
  };

  const { volumeIconSrc, volumeTitle } = getVolumeIcon(volume);
  const numberOfPlayers = Object.values(players).length;

  return {
    player,
    connection,
    muted,
    volume,
    isVolumeTooltipOpen,
    setIsVolumeTooltipOpen,
    progressPercent,
    progressSeconds,
    currentSongEntry,
    ratedSongs,
    triggerMuted,
    rate,
    changeVolume,
    postChangeVolume,
    volumeIconSrc,
    volumeTitle,
    numberOfPlayers
  };
};
